import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CorrectionResult } from '@/utils/sajuCalculations';

interface ResultReceiptProps {
  correctionData: CorrectionResult;
  year: number;
  locationName: string;
  locationAddress: string;
  onNext: () => void;
}

export default function ResultReceipt({
  correctionData,
  year,
  locationName,
  locationAddress,
  onNext,
}: ResultReceiptProps) {
  const { t } = useTranslation();
  const [visibleItems, setVisibleItems] = useState<number>(0);

  const { dstActive } = correctionData;
  const dstNote = dstActive ? `${year}${t('receipt.dst.note')}` : null;

  useEffect(() => {
    const items = dstNote ? 5 : 4;
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setVisibleItems(i);
      if (i >= items) clearInterval(timer);
    }, 350);
    return () => clearInterval(timer);
  }, [dstNote]);

  const correctedH = correctionData.correctedHour;
  const correctedM = correctionData.correctedMinute;
  const inputH = correctionData.inputHour;
  const inputM = correctionData.inputMinute;

  const fmtTime = (h: number, m: number) =>
    `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;

  const longSign = correctionData.longitudeCorrection >= 0 ? '+' : '';
  const eotSign = correctionData.eotCorrection >= 0 ? '+' : '';
  const totalSign = correctionData.totalCorrectionMinutes >= 0 ? '+' : '';

  const dirNote = correctionData.longitudeCorrection >= 0
    ? t('receipt.east.note')
    : t('receipt.west.note');

  const receiptItems = [
    {
      icon: 'ri-time-line',
      color: 'text-gray-600',
      label: t('receipt.inputTime'),
      value: fmtTime(inputH, inputM),
      highlight: false,
    },
    {
      icon: 'ri-map-pin-2-line',
      color: 'text-amber-500',
      label: `${t('receipt.longitudeCorr')} (${locationName})`,
      value: `${longSign}${correctionData.longitudeCorrection}min`,
      note: dirNote,
      highlight: false,
    },
    {
      icon: 'ri-sun-line',
      color: 'text-amber-500',
      label: t('receipt.eotCorr'),
      value: `${eotSign}${correctionData.eotCorrection}min`,
      note: t('receipt.eotNote'),
      highlight: false,
    },
    ...(dstNote ? [{
      icon: 'ri-alarm-warning-line',
      color: 'text-red-400',
      label: t('receipt.dstCorr'),
      value: '−60min',
      note: dstNote,
      highlight: false,
    }] : []),
    {
      icon: 'ri-sparkling-2-line',
      color: 'text-gray-900',
      label: t('receipt.trueTime'),
      value: fmtTime(correctedH, correctedM),
      highlight: true,
    },
  ];

  return (
    <div
      className="flex flex-col bg-gray-50"
      style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* 상단 로고 - 고정 영역 */}
      <div className="flex items-center justify-between px-5 py-4">
        <img
          src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png"
          alt="바른사주"
          className="h-7 object-contain"
        />
        <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-green-500 text-xs" />
          </div>
          <span className="text-xs font-semibold whitespace-nowrap">{t('receipt.corrected')}</span>
        </div>
      </div>

      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto px-5 pb-4">
        <div className="max-w-sm mx-auto w-full">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5 leading-snug">
            {t('receipt.title').split('\n').map((line, i) => (
              <span key={i}>{line}{i < t('receipt.title').split('\n').length - 1 && <br />}</span>
            ))}
          </h2>
          <p className="text-gray-400 text-sm mb-5">{locationAddress}</p>

          {/* 영수증 카드 */}
          <div className="bg-white rounded-3xl overflow-hidden mb-4 border border-gray-100">
            {/* 영수증 헤더 */}
            <div className="bg-gray-900 px-5 py-3.5 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-receipt-line text-white text-base" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">{t('receipt.header')}</p>
                <p className="text-white/50 text-xs">{year}{t('receipt.birthYear')}</p>
              </div>
            </div>

            {/* 보정 내역 */}
            <div className="px-5 py-3 space-y-1">
              {receiptItems.map((item, idx) => {
                const visible = idx < visibleItems;
                return (
                  <div
                    key={idx}
                    className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                  >
                    {idx === receiptItems.length - 1 && (
                      <div className="border-t-2 border-dashed border-gray-200 my-3" />
                    )}
                    <div className={`flex items-start gap-3 py-2 ${item.highlight ? 'bg-gray-50 -mx-5 px-5 rounded-xl' : ''}`}>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} ${item.color} text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${item.highlight ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                          {item.label}
                        </p>
                        {item.note && (
                          <p className="text-xs text-gray-400 mt-0.5">{item.note}</p>
                        )}
                      </div>
                      <span className={`font-bold text-sm ml-2 flex-shrink-0 ${
                        item.highlight ? 'text-gray-900 text-base' : 'text-gray-700'
                      }`}>
                        {item.value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 총 보정량 */}
            <div className="bg-amber-50 px-5 py-3 border-t border-amber-100">
              <div className="flex items-center justify-between">
                <span className="text-amber-700 text-xs font-medium">{t('receipt.totalCorr')}</span>
                <span className="text-amber-700 font-bold text-sm">
                  {totalSign}{correctionData.totalCorrectionMinutes}min
                </span>
              </div>
            </div>
          </div>

          {/* 시주 변경 알림 */}
          {correctionData.branchChanged && visibleItems >= receiptItems.length && (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-4 mb-4 flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-xl flex-shrink-0">
                <i className="ri-alert-line text-red-500 text-sm" />
              </div>
              <div>
                <p className="text-red-700 font-bold text-sm mb-1">{t('receipt.branchChanged.title')}</p>
                <p className="text-red-600 text-xs leading-relaxed">
                  {t('receipt.branchChanged.body1')}<strong>'{correctionData.inputBranch}'</strong>
                  {t('receipt.branchChanged.body2')}<strong>'{correctionData.correctedBranch}'</strong>
                  {t('receipt.branchChanged.body3')}
                </p>
              </div>
            </div>
          )}

          {/* 표준시 안내 */}
          {visibleItems >= receiptItems.length && (
            <div className="bg-gray-100 rounded-2xl p-3 flex items-center gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-gray-500 text-sm" />
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">{correctionData.timezoneNote}</p>
            </div>
          )}
        </div>
      </div>

      {/* 하단 고정 버튼 영역 */}
      <div
        className="px-5 pt-3 bg-gray-50 border-t border-gray-100"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-sm mx-auto w-full">
          <button
            onClick={onNext}
            disabled={visibleItems < receiptItems.length}
            className={`w-full font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
              visibleItems >= receiptItems.length
                ? 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95'
                : 'bg-gray-200 text-gray-400'
            }`}
          >
            {t('receipt.cta')} <i className="ri-arrow-right-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}
